/**
 * The dispatch board — the screen an ops desk lives in.
 *
 * **Sorted by ceremony time, always.** The board's question is "what is about to go wrong", and a
 * muhurat does not move to suit a queue. The server orders it; this screen does not re-sort.
 *
 * **Every filter is a query parameter.** The tabs, the city, the date window and the `BMP…` box
 * all go to the server, because the list is paged and a client-side `.filter()` would search the
 * fifty rows in hand and then confidently report no match on the other four hundred.
 *
 * **The buttons come from the server.** `GET …/moves` says which transitions an admin may fire
 * from where this booking is standing, and the override offers exactly those. A list written here
 * would 409 the day an edge changed — and the version nobody notices is the button that quietly
 * stopped being offered.
 *
 * **Dispatch itself lives elsewhere.** Waves, offers and manual matching are the matching console's
 * (B23) and replacements have their own queue (B24). The tab that used to show wave history here
 * showed invented waves; it went rather than being disabled, and the footer points to where the
 * real thing is.
 */
import { useState } from 'react';
import { Note, StatusChip, type Tone } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable, DetailPage } from '../components/data';
import { KV, PriceBreakdown, Timeline } from '../components/entities';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, Money, ScopeAction, Section, UrgencyBanner } from '../components/kit';
import { formatInr } from '../lib/money';
import { PhaseLanes, racingDeadline } from './PhaseLanes';
import { useConsole } from '../app/console';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { CodeOverrideAction } from './CodeOverride';
import { CancellationQuotePanel } from './CancellationQuote';
import { errorMessage } from '../lib/api/client';
import { useCities } from '../lib/api/hooks/geo';
import {
  useAdminCancelBooking,
  useBooking,
  useBookingBoard,
  useBookingMoves,
  useBookingTimeline,
  useForceBookingStatus,
  useRevealPrivate,
  type BookingDetail,
  type BookingMove,
} from '../lib/api/hooks/bookings';
import {
  useEvidenceGrants,
  useGrantEvidence,
  useLegalHoldEvidence,
  useOpenEvidence,
  useRevokeEvidenceGrant,
} from '../lib/api/hooks/evidence';
import { HealthStrip } from './HealthStrip';
import { useBoardPollMs, useUrgentWindowMinutes } from '../lib/api/hooks/settings';
import { AuditTrailPanel } from '../components/audit';

const PAGE_SIZE = 50;
/** The lanes are one view of every working state, so they ask for the server's largest page. */
const LANE_PAGE_SIZE = 100;

/** The absence of a city filter, spelled out — a blank Select reads as a field somebody forgot. */
const ALL_CITIES = 'All cities';

/**
 * The statuses the phase lanes are built from — what the Lanes tab asks the server for. Kept
 * here rather than in `PhaseLanes.tsx`: that module imports this one's formatters, and the tab
 * list below reads this constant while the module is still loading.
 */
export const LANE_STATUSES = [
  'requested',
  'matching',
  'accepted',
  'payment_pending',
  'reassignment_required',
  'no_provider_available',
  'acceptance_expired',
  'confirmed',
  'provider_en_route',
  'in_progress',
  'completed',
  'issue_window',
] as const;

/**
 * The tabs, and the statuses each one selects on the server.
 *
 * `esc` is what the board opens on: a booking nobody has taken and a booking whose pandit walked
 * are the two shapes of trouble, and both are somebody's ceremony this week.
 */
export const BOARD_TABS = [
  { key: 'esc', label: 'Escalations', statuses: ['no_provider_available', 'reassignment_required'] },
  { key: 'unacc', label: 'Unaccepted', statuses: ['requested', 'matching', 'acceptance_expired'] },
  { key: 'live', label: 'Confirmed', statuses: ['confirmed', 'provider_en_route', 'in_progress'] },
  { key: 'settle', label: 'Settling', statuses: ['completed', 'issue_window', 'payout_approved'] },
  { key: 'all', label: 'All', statuses: [] },
  // The phase-lane worklist (doc 08 §3.1 item 3), built from the doc by the 2026-10-26 ruling.
  { key: 'lanes', label: 'Lanes', statuses: LANE_STATUSES },
] as const;
const BOARD_TAB_KEYS = BOARD_TABS.map((t) => t.key);

export function tabStatuses(tab: string): readonly string[] {
  return BOARD_TABS.find((t) => t.key === tab)?.statuses ?? [];
}

/**
 * How alarming a status is, in the colour an operator reads before the words.
 *
 * Derived from the status rather than sent by the server: what counts as trouble is an ops
 * judgement about this console, not a fact about the booking.
 */
export function statusTone(status: string): Tone {
  if (status === 'no_provider_available' || status === 'reassignment_required') return 'error';
  if (status === 'requested' || status === 'matching' || status === 'acceptance_expired')
    return 'warn';
  if (status === 'settled' || status === 'completed' || status === 'confirmed') return 'success';
  return 'info';
}

/**
 * The chip for one booking. A booking whose payment is captured but held for a risk review
 * (08-payments/15 §2) is still `payment_pending` underneath; the server says so with `confirming`,
 * and "payment pending" would send ops chasing a devotee who has already paid.
 */
export function bookingChip(b: { status: string; confirming?: boolean }): { tone: Tone; label: string } {
  if (b.confirming) return { tone: 'warn', label: 'held for review' };
  return { tone: statusTone(b.status), label: statusLabel(b.status) };
}

/** `no_provider_available` reads as a database value; ops reads sentences. */
export function statusLabel(status: string): string {
  return status.replaceAll('_', ' ');
}

/**
 * `resolution_reason` in ops words. The enum_label rows speak to the devotee ("Cancelled by
 * you"), which reads wrong on a console; the rest are plain enough with the underscores gone.
 */
const ENDED_BECAUSE: Readonly<Record<string, string>> = {
  customer_paid: 'Devotee cancelled after paying',
  customer_prepay: 'Devotee cancelled before paying',
};

export function endedBecause(reason: string): string {
  return ENDED_BECAUSE[reason] ?? statusLabel(reason);
}

/**
 * Time to the ceremony, which is the only clock that matters here.
 *
 * Negative means it has already started — shown as such rather than clamped to zero, because a
 * booking still in `matching` two hours after its muhurat is exactly the row ops must see.
 */
export function clockTo(startAt: string, now: number = Date.now()): string {
  const minutes = Math.round((new Date(startAt).getTime() - now) / 60000);
  const sign = minutes < 0 ? '−' : '';
  const abs = Math.abs(minutes);
  if (abs < 60) return `${sign}${abs}m`;
  if (abs < 60 * 48) return `${sign}${Math.floor(abs / 60)}h ${abs % 60}m`;
  return `${sign}${Math.floor(abs / 1440)}d`;
}

/** IST, because every ceremony on this board happens in it. */
export function ceremonyAt(startAt: string): string {
  return new Date(startAt).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const TABS = ['Overview', 'Money', 'Timeline', 'Evidence', 'History'];

/** The server's closed set (`AdminCancelReason`). None of them is the devotee's doing. */
const CANCEL_REASONS = ['safety', 'fraud', 'force_majeure', 'ops_error'] as const;

/**
 * The cancel edge, kept out of the override buttons. Forcing it would cancel with no refund; the
 * cancel route raises the full refund in the same transaction, so that is the only way it is fired.
 */
const CANCEL_TRIGGER = 'admin_cancelled';

/**
 * The parties panel. Address and participants arrive sealed (admin doc 08 §3.3 item 3): before the
 * booking day the panel says when they open; on it, a reveal with a reason fetches them, and that
 * click is on the devotee's access record.
 */
function Overview({ booking }: { booking: BookingDetail }) {
  const { scope, toast } = useConsole();
  const reveal = useRevealPrivate();
  const [asking, setAsking] = useState(false);
  const shown = reveal.data;
  const sealed = booking.private_sealed && !shown;
  const participants = shown?.participants ?? [];
  const yajman = participants.find((p) => p.is_primary) ?? participants[0];
  const sealedLine = booking.private_window_open ? (
    <span key="sealed" style={{ display: 'inline-flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
      Sealed
      <ScopeAction scope={scope} need={['ops_dispatch', 'support']} size="sm" variant="secondary" onClick={() => setAsking(true)}>
        Reveal (audited)
      </ScopeAction>
    </span>
  ) : (
    'Sealed — visible on the booking day'
  );
  const venue = shown?.address
    ? `${shown.address.address_line1}, ${shown.address.pincode}`
    : sealed && booking.service_mode === 'at_home'
      ? sealedLine
      : 'Online — nobody travels';
  return (
    <>
      <KV
        rows={[
          ['Yajman', yajman ? `${yajman.full_name} · ${yajman.relation}` : sealed ? sealedLine : '—'],
          ['Ceremony', `${ceremonyAt(booking.scheduled_start_at)} IST`],
          ['Runs for', `${booking.scheduled_duration_minutes} minutes`],
          ['Venue', venue],
          ['Service', statusLabel(booking.service_mode)],
          ['Samagri', booking.samagri_source === 'platform' ? 'We bring it' : 'The family brings it'],
          ['Attending', String(booking.attendee_count)],
          ['Total', <Money key="total" paise={booking.total_paise} showPaise />],
          ...(booking.resolution_reason ? [['Ended because', endedBecause(booking.resolution_reason)] as const] : []),
        ]}
      />
      {shown && (
        <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
          Revealed to you — this access is on the devotee&apos;s record with your reason.
        </span>
      )}
      <ConfirmAction
        open={asking}
        title="Reveal the address and the family's details?"
        phrase="REVEAL"
        reason="Why you need them now (kept on the access record)"
        consequences={[
          'Your name, your reason and the time are written to this devotee’s access record.',
          'Only for today’s visit — the details close again after the booking day.',
        ]}
        confirmLabel="Reveal"
        onCancel={() => setAsking(false)}
        onConfirm={(reason: string) => {
          setAsking(false);
          reveal.mutate(
            { bookingId: booking.id, reason },
            { onError: (e: unknown) => toast({ tone: 'error', message: errorMessage(e) }) },
          );
        }}
      />
    </>
  );
}


/**
 * Who may open this booking's private evidence, and the way to look at it.
 *
 * **Nothing is listed until somebody is granted.** The panel does not fetch the evidence on
 * mount: reading it writes an access-log row, and a screen that minted links merely because a
 * tab was open would fill a devotee's access trail with looks nobody took. Opening is an act.
 *
 * **Revoked grants stay on the list.** "Who has access" and "who has ever had access" are
 * different questions, and the second is the one asked after something has gone wrong.
 */
function EvidencePanel({ code }: { code: string }) {
  const { scope, toast } = useConsole();
  const grants = useEvidenceGrants(code);
  const grant = useGrantEvidence();
  const revoke = useRevokeEvidenceGrant();
  const open = useOpenEvidence();
  const hold = useLegalHoldEvidence();
  const [holding, setHolding] = useState(false);
  const [adminId, setAdminId] = useState('');
  const [reason, setReason] = useState('support_case');
  const [revoking, setRevoking] = useState<number | null>(null);

  const fail = (error: unknown) => toast({ tone: 'error', message: errorMessage(error) });

  if (grants.isPending) return <LoadingState message="Reading who has access…" />;
  if (grants.isError) {
    return (
      <ErrorState title="Could not read the grants" cause={errorMessage(grants.error)} />
    );
  }

  const rows = grants.data.grants;
  return (
    <>
      <Section
        title="Open the evidence"
        note="Every look is written into the devotee's access trail"
        actions={
          <ScopeAction
            scope={scope}
            need="support"
            size="sm"
            variant="secondary"
            disabled={open.isPending}
            onClick={() =>
              open.mutate(
                { code },
                {
                  onSuccess: (list) => {
                    for (const item of list.evidence) {
                      window.open(item.url, '_blank', 'noopener');
                    }
                  },
                  onError: fail,
                },
              )
            }
          >
            Open under my grant
          </ScopeAction>
        }
      >
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
          Being support does not open anything — a live grant does. If you have none, the server
          answers exactly as it would for a booking that does not exist.
        </p>
      </Section>

      <Section
        title="Download for legal hold"
        note="Superadmin only — the reason is written on its own audit row"
        actions={
          <ScopeAction
            scope={scope}
            need="superadmin"
            size="sm"
            variant="secondary"
            disabled={hold.isPending}
            onClick={() => setHolding(true)}
          >
            Download for legal hold
          </ScopeAction>
        }
      >
        <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
          The download is logged in the devotee's access trail like any other look, under a grant
          that lasts only as long as the links.
        </p>
      </Section>

      <Section title="Give somebody access" note="One person, this booking, with an end">
        <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}>
          <Input label="Admin id" value={adminId} onChange={setAdminId} />
          <Select
            label="Why"
            value={reason}
            options={['support_case', 'dispute_review']}
            onChange={setReason}
          />
          <ScopeAction
            scope={scope}
            need="support"
            size="sm"
            disabled={adminId.trim() === '' || grant.isPending}
            onClick={() =>
              grant.mutate(
                { code, adminId: Number(adminId), reason },
                {
                  onSuccess: () => {
                    setAdminId('');
                    toast({ tone: 'success', message: 'Access granted, with an expiry' });
                  },
                  onError: fail,
                },
              )
            }
          >
            Grant
          </ScopeAction>
        </div>
      </Section>

      <Section title="Grants" note="Revoked ones stay — the history is the point">
        {rows.length === 0 ? (
          <EmptyState
            title="Nobody has been granted access"
            body="The devotee and the pandit who performed the puja hold their own grants; staff hold none until one is written here."
          />
        ) : (
          <DataTable
            density="compact"
            columns={[
              { key: 'who', label: 'Who' },
              { key: 'why', label: 'Why' },
              { key: 'until', label: 'Until' },
              { key: 'act', label: '' },
            ]}
            rows={rows.map((row) => ({
              id: row.id,
              who: row.grantee_type,
              why: row.granted_reason,
              until: row.revoked_at
                ? `revoked — ${row.revoke_reason ?? 'no reason recorded'}`
                : row.expires_at
                  ? ceremonyAt(row.expires_at)
                  : 'until revoked',
              act: row.revoked_at ? null : (
                <ScopeAction
                  scope={scope}
                  need="support"
                  size="sm"
                  variant="ghost"
                  onClick={() => setRevoking(row.id)}
                >
                  Revoke
                </ScopeAction>
              ),
            }))}
          />
        )}
      </Section>

      {holding && (
        <ConfirmAction
          open
          title="Download this booking's evidence for a legal hold?"
          confirmLabel="Download"
          phrase="HOLD"
          consequences={[
            'Every photograph and video on this booking opens in a new tab for you to save.',
            "The download is written into the devotee's access trail and into the audit log with your reason.",
          ]}
          reason="The matter this hold is for — this is written down"
          onCancel={() => setHolding(false)}
          onConfirm={(why) =>
            hold.mutate(
              { code, reason: why },
              {
                onSuccess: (list) => {
                  setHolding(false);
                  for (const item of list.evidence) {
                    window.open(item.url, '_blank', 'noopener');
                  }
                },
                onError: (error) => {
                  setHolding(false);
                  fail(error);
                },
              },
            )
          }
        />
      )}

      {revoking !== null && (
        <ConfirmAction
          open
          title="Revoke this grant?"
          confirmLabel="Revoke"
          phrase="REVOKE"
          consequences={[
            'The grant stops opening anything on the next read — not on the next sweep.',
            'The row stays on the list, with your reason, because who HAD access is the question asked after something goes wrong.',
          ]}
          reason="Why — this is written down"
          onCancel={() => setRevoking(null)}
          onConfirm={(why) =>
            revoke.mutate(
              { grantId: revoking, reason: why },
              {
                onSuccess: () => {
                  setRevoking(null);
                  toast({ tone: 'success', message: 'Revoked — it stops on the next read' });
                },
                onError: (error) => {
                  setRevoking(null);
                  fail(error);
                },
              },
            )
          }
        />
      )}
    </>
  );
}

/** §5.6 on the booking detail: the banner over every tab while its deadline is close or past. */
function DetailUrgency({ booking, urgentWithinMinutes }: { booking: BookingDetail; urgentWithinMinutes: number | undefined }) {
  const deadline = racingDeadline(booking);
  if (!deadline) return null;
  return (
    <UrgencyBanner until={deadline.until} label={deadline.label} urgentWithinMinutes={urgentWithinMinutes}>
      {deadline.label === 'window'
        ? 'The issue window is closing — anything the devotee raised must be answered first.'
        : deadline.label === 'payment due'
          ? 'The payment deadline is close — the hold lapses if the devotee does not pay.'
          : 'The ceremony is close and the booking is not confirmed yet.'}
    </UrgencyBanner>
  );
}

export function DispatchBoard() {
  const { scope, toast } = useConsole();
  const urgentWithin = useUrgentWindowMinutes();
  const pollMs = useBoardPollMs();
  const [tab, setTab] = useUrlTab('dispatch', BOARD_TAB_KEYS, 'esc');
  const [q, setQ] = useUrlSearch('dispatch');
  const [city, setCity] = useState(ALL_CITIES);
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState('Overview');
  const [move, setMove] = useState<BookingMove | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState<string>('ops_error');

  // Launched cities only: an operator cannot dispatch into one we do not run in, so offering it
  // as a filter would be offering a view that is always empty.
  const cities = useCities({ page: 1, page_size: 100 });
  const cityId = cities.data?.items.find((c) => c.name === city)?.id;

  const board = useBookingBoard({
    // A `BMP…` code is an exact match, and searching for one inside a tab means "find it if it
    // happens to be in this state" — which is never what somebody reading a code aloud wants.
    status: q ? [] : tabStatuses(tab),
    city_id: cityId,
    q: q || undefined,
    page: tab === 'lanes' && !q ? 1 : page,
    page_size: tab === 'lanes' && !q ? LANE_PAGE_SIZE : PAGE_SIZE,
  }, true, pollMs);
  const booking = useBooking(openId);
  const moves = useBookingMoves(openId);
  const timeline = useBookingTimeline(openId);
  const force = useForceBookingStatus();
  const cancel = useAdminCancelBooking();

  const rows = board.data?.items ?? [];
  const total = board.data?.total ?? 0;

  const commit = (bookingId: number, chosen: BookingMove, reason: string) => {
    force.mutate(
      { bookingId, to_status: chosen.to_status, trigger: chosen.trigger, reason },
      {
        onSuccess: (updated) => {
          setMove(null);
          toast({ tone: 'info', message: `Moved to ${statusLabel(updated.status)} — recorded.` });
        },
        // The server's refusal, verbatim: it knows which guard stopped it and this screen does not.
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );
  };

  if (openId !== null) {
    // Captured once: the callbacks below close over it, and TypeScript cannot narrow state
    // inside a closure — which is how a guard nothing can reach gets written.
    const id = openId;
    return (
      <DetailPage
        title={booking.data ? `Booking · ${statusLabel(booking.data.service_mode)}` : 'Booking'}
        code={booking.data?.public_code}
        status={
          booking.data ? (
            <StatusChip {...bookingChip(booking.data)} />
          ) : undefined
        }
        tabs={TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => setOpenId(null)}
        actions={
          <>
            {/* Exactly the moves the server says exist from here — never a list written above. */}
            {(moves.data?.items ?? []).filter((m) => m.trigger !== CANCEL_TRIGGER).map((m) => (
              <ScopeAction
                key={m.trigger}
                scope={scope}
                need="superadmin"
                size="sm"
                variant="secondary"
                onClick={() => setMove(m)}
              >
                {statusLabel(m.to_status)}
              </ScopeAction>
            ))}
            {booking.isSuccess && (
              <CodeOverrideAction code={booking.data.public_code} status={booking.data.status} />
            )}
            {booking.isSuccess && moves.data?.items.some((m) => m.trigger === CANCEL_TRIGGER) && (
              <ScopeAction
                scope={scope}
                need="finance"
                size="sm"
                variant="secondary"
                onClick={() => setCancelling(true)}
              >
                Cancel booking
              </ScopeAction>
            )}
          </>
        }
      >
        {booking.isPending && <LoadingState message="Reading the booking…" />}
        {booking.isError && (
          <ErrorState title="Could not read the booking" cause={errorMessage(booking.error)} />
        )}
        {booking.isSuccess && <DetailUrgency booking={booking.data} urgentWithinMinutes={urgentWithin} />}
        {booking.isSuccess && detailTab === 'Overview' && <Overview booking={booking.data} />}

        {booking.isSuccess && detailTab === 'Money' && (
          <>
            <PriceBreakdown
              variant="admin"
              total={formatInr(booking.data.total_paise, { paise: true })}
              lines={booking.data.line_items.map((l) => ({
                label: l.label,
                amount: formatInr(l.amount_paise, { paise: true }),
              }))}
              footnote="Copied at checkout from the published sheet — a price change since cannot reach it."
            />
            <CancellationQuotePanel code={booking.data.public_code} />
          </>
        )}

        {detailTab === 'Timeline' && (
          <>
            {timeline.isPending && <LoadingState message="Reading the trail…" />}
            {timeline.isError && (
              <ErrorState title="Could not read the trail" cause={errorMessage(timeline.error)} />
            )}
            {timeline.isSuccess && (
              <Timeline
                events={timeline.data.items.map((row, index) => ({
                  title: statusLabel(row.to_status),
                  timestamp: ceremonyAt(row.created_at),
                  detail: [
                    row.trigger_code,
                    `by ${row.actor_type}`,
                    row.context?.override ? 'overridden by hand' : null,
                    row.reason,
                  ]
                    .filter(Boolean)
                    .join(' · '),
                  state: index === timeline.data.items.length - 1 ? 'current' : 'done',
                }))}
              />
            )}
          </>
        )}

        {booking.isSuccess && detailTab === 'History' && <AuditTrailPanel entityType="booking" entityId={booking.data.id} />}
        {booking.isSuccess && detailTab === 'Evidence' && (
          <EvidencePanel code={booking.data.public_code} />
        )}

        {moves.isSuccess && moves.data.items.length === 0 && detailTab !== 'Evidence' && (
          <Section title="Nothing to move" note="This booking has reached a state with no way out">
            <p style={{ margin: 0, fontSize: 'var(--fs-admin-ui)', color: 'var(--c-ink2)' }}>
              The state machine offers no transition from {statusLabel(moves.data.status)}.
            </p>
          </Section>
        )}

        {move !== null && (
          <ConfirmAction
            open
            title={`Move this booking to ${statusLabel(move.to_status)}?`}
            phrase="OVERRIDE"
            tone="error"
            reason="Why this booking is being moved by hand"
            consequences={[
              move.note,
              'This is a manual override. It is recorded against your name in the audit log and in the booking’s own trail, permanently.',
              'The transition fires now — notifications, sweeps and money that depend on the new state follow from it.',
            ]}
            confirmLabel="Move it"
            onCancel={() => setMove(null)}
            onConfirm={(reason) => commit(id, move, reason)}
          />
        )}
        {cancelling && booking.isSuccess && (
          <ConfirmAction
            open
            title={`Cancel ${booking.data.public_code}?`}
            // The code, not a stock word: typing it is reading which booking this is (doc 08 §6).
            phrase={booking.data.public_code}
            tone="error"
            reason="Memo (required, kept with the cancellation)"
            consequences={[
              `Refunds the devotee in full — ${formatInr(booking.data.total_paise, { paise: true })} (matrix row M9). The cancellation window does not apply to a cancel made by us.`,
              // Backend 28 §5.3: a platform cancellation is the platform's cost. The fee is not on
              // the booking, so the line names who bears it and no amount.
              'Razorpay keeps its fee on the refunded payment; the platform bears it, since the cancel is ours.',
              'The pandit is released and the booking cannot be reopened.',
            ]}
            confirmLabel="Cancel booking"
            onCancel={() => setCancelling(false)}
            onConfirm={(memo) =>
              cancel.mutate(
                { code: booking.data.public_code, reason_code: cancelReason, memo },
                {
                  onSuccess: (result) => {
                    setCancelling(false);
                    toast({
                      tone: 'success',
                      message: result.refund
                        ? `Cancelled — refund ${result.refund.public_code} raised`
                        : 'Cancelled — nothing had been paid, so nothing is refunded',
                    });
                  },
                  onError: (e) => {
                    setCancelling(false);
                    toast({ tone: 'error', message: errorMessage(e) });
                  },
                },
              )
            }
          >
            <Select
              label="Reason"
              value={cancelReason}
              options={CANCEL_REASONS}
              onChange={setCancelReason}
            />
          </ConfirmAction>
        )}
      </DetailPage>
    );
  }

  return (
    <>
      <HealthStrip
        cityId={cityId}
        onEscalations={() => {
          setTab('esc');
          setQ('');
          setPage(1);
        }}
      />
      <FilterBar
        q={q}
        onQ={(value) => {
          setQ(value);
          setPage(1);
        }}
        placeholder="BMP code — press /"
        tab={tab}
        onTab={(next) => {
          setTab(next);
          setPage(1);
        }}
        tabs={BOARD_TABS.map((t) => ({ key: t.key, label: t.label }))}
        extra={
          <Select
            density="admin"
            ariaLabel="City"
            value={city}
            options={[ALL_CITIES, ...(cities.data?.items ?? []).map((c) => c.name)]}
            onChange={(next) => {
              setCity(next);
              setPage(1);
            }}
            style={{ width: 160 }}
          />
        }
      />

      {board.isPending && <LoadingState message="Reading the board…" />}
      {board.isError && (
        <ErrorState title="Could not read the board" cause={errorMessage(board.error)} />
      )}
      {board.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="calendar-clock"
          title={q ? 'No booking with that code' : 'Nothing in this state'}
          body={
            q
              ? 'The code is matched exactly, across every state — so this is genuinely not one of ours.'
              : 'Every booking has moved on from here.'
          }
          inline
        />
      )}

      {rows.length > 0 && tab === 'lanes' && !q && (
        <PhaseLanes
          rows={rows}
          total={total}
          urgentWithinMinutes={urgentWithin}
          onOpen={(id) => {
            setOpenId(id);
            setDetailTab('Overview');
          }}
        />
      )}

      {rows.length > 0 && (tab !== 'lanes' || q) && (
        <DataTable
          density="compact"
          onRowClick={(row) => {
            setOpenId(Number(row.id));
            setDetailTab('Overview');
          }}
          emptyLabel="No bookings match this filter"
          footer={
            <>
              <span>
                {(page - 1) * PAGE_SIZE + 1}–{(page - 1) * PAGE_SIZE + rows.length} of {total}
              </span>
              <span style={{ flex: 1 }} />
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="ghost"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </ScopeAction>
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="ghost"
                disabled={page * PAGE_SIZE >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </ScopeAction>
            </>
          }
          columns={[
            { key: 'code', label: 'Booking', mono: true },
            { key: 'when', label: 'Ceremony', sort: 'asc' },
            { key: 'mode', label: 'Service' },
            { key: 'status', label: 'State' },
            { key: 'clock', label: 'Clock', align: 'right', mono: true },
            { key: 'amount', label: 'Amount', align: 'right', mono: true },
          ]}
          rows={rows.map((b) => ({
            id: b.id,
            code: b.public_code,
            when: ceremonyAt(b.scheduled_start_at),
            mode: statusLabel(b.service_mode),
            status: <StatusChip {...bookingChip(b)} />,
            clock: clockTo(b.scheduled_start_at),
            amount: <Money paise={b.total_paise} showPaise />,
          }))}
        />
      )}

      <Note>
        Sorted by ceremony time · this board reads bookings only — dispatch waves and manual
        matching are on the matching console, and replacements have their own queue
      </Note>
    </>
  );
}
